<template>
  <div v-show="ready" class="bg-white rounded-[12px] border border-gray-200 shadow-custom overflow-hidden mb-5 cv-vignette-prices-ready">
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200">
            <th class="text-left px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider">
              {{ headerValidity }}
            </th>
            <th class="text-right px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider">
              {{ headerOfficial }}
            </th>
            <th class="text-right px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider">
              {{ headerFee }}
            </th>
            <th class="text-right px-6 py-4 font-bold text-gray-500 text-xs uppercase tracking-wider bg-blue-50">
              {{ headerTotal }}
            </th>
            <th class="px-6 py-4"></th>
          </tr>
        </thead>
        <tbody v-if="rows.length">
          <tr
            v-for="(row, index) in rows"
            :key="row.id"
            :class="['hover:bg-gray-50 transition-colors', index === rows.length - 1 ? '' : 'border-b border-gray-100']"
          >
            <td class="px-6 py-5">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                  <MaterialSymbol :name="row.icon" class="text-blue-900 text-lg" />
                </div>
                <span class="font-bold text-gray-900">{{ row.label }}</span>
              </div>
            </td>
            <td class="px-6 py-5 text-right text-gray-600 font-medium">
              <span v-if="showPrices">{{ formatPrice(row.official) }}</span>
              <span v-else class="inline-block min-w-[4rem] text-gray-300" aria-hidden="true">&nbsp;</span>
            </td>
            <td class="px-6 py-5 text-right text-gray-600 font-medium">
              <span v-if="showPrices">{{ formatPrice(row.fee) }}</span>
              <span v-else class="inline-block min-w-[4rem] text-gray-300" aria-hidden="true">&nbsp;</span>
            </td>
            <td class="px-6 py-5 text-right bg-blue-50">
              <span v-if="showPrices" class="font-black text-blue-900 text-base">
                {{ formatPrice(row.total) }}
              </span>
              <span v-else class="inline-block min-w-[4rem] text-gray-300" aria-hidden="true">&nbsp;</span>
            </td>
            <td class="px-6 py-5 text-right">
              <button
                type="button"
                class="bg-buy-green hover:bg-buy-green-hover text-white px-4 py-2 rounded-small font-bold text-xs transition-all whitespace-nowrap"
                @click="onBuy(row)"
              >
                {{ ctaLabel }}
              </button>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="5" class="px-6 py-5 text-center text-gray-500 text-sm">
              {{ emptyLabel }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
    getProductBasePrice,
    getProductServiceFee,
    getProductTotalWithFees,
} from '../utils/productPricing.js';
import { resolveBuyVignetteHref } from '../utils/buyVignetteFallbackUrl.js';

defineOptions({ name: 'VignettePrices' });

const store = useStore();

/** Fuel vignette CMS page_ids — mirrors PaymentMethodsReadyPrices / SSR. */
const FUEL_PAGE_IDS = { 1: 72, 10: 77, 30: 82, 365: 87 };
const TARGET_DAYS = [1, 10, 30, 365];

const products = computed(() => store.getters['form/products'] || []);
const categorizedProducts = computed(() => store.getters['form/categorizedProducts'] || []);
const texts = computed(() => store.getters.texts || {});
const storeCurrency = computed(() => store.getters['currency/currentCurrency'] || {});
const currencies = computed(() => store.getters.currencies || []);
const props = defineProps({
    buyVignetteHref: {
        type: String,
        default: ''
    }
});
const buyUrl = computed(() => resolveBuyVignetteHref(props.buyVignetteHref));
const ready = ref(false);
const pricesLoading = ref(false);

const catalogCurrencyCode = computed(() =>
    String(store.state.form?.productsCurrencyCode ?? '').trim().toUpperCase()
);

const showPrices = computed(() => {
    if (!ready.value || pricesLoading.value) return false;
    const wanted = String(storeCurrency.value?.code ?? '').trim().toUpperCase();
    if (!wanted) return true;
    return catalogCurrencyCode.value === wanted;
});
const currencySymbol = computed(() =>
    storeCurrency.value?.symbol ||
    currencies.value[0]?.symbol ||
    '€'
);
const headerValidity = computed(() =>
    texts.value['vignette-prices.blade.php-th-validity'] ?? 'Validity'
);

const headerOfficial = computed(() =>
    texts.value['vignette-prices.blade.php-th-official'] ?? 'Official Price'
);

const headerFee = computed(() =>
    texts.value['vignette-prices.blade.php-th-fee'] ?? 'Service Fee'
);

const headerTotal = computed(() =>
    texts.value['vignette-prices.blade.php-th-total'] ?? 'Total Price'
);

const ctaLabel = computed(() =>
    texts.value['vignette-prices.blade.php-cta-buy'] ?? 'Buy Now'
);

const emptyLabel = computed(() =>
    texts.value['vignette-prices.blade.php-empty'] ?? 'No vignette options available.'
);

/**
 * Rows for table — one fuel product per standard duration (mirrors SSR + PaymentMethodsReadyPrices).
 */
const rows = computed(() => {
    return TARGET_DAYS.map((days) => {
        const { product, categoryRow } = pickFuelEntry(days);
        if (!product) return null;

        const label = resolveLabelFromApi(product, categoryRow) || `${days} days`;
        const { official, fee, total } = getPriceParts(product);

        return {
            id: product.id ?? null,
            label,
            icon: pickIcon(days),
            days,
            official,
            fee,
            total,
        };
    }).filter(Boolean);
});

function pickFuelEntry(days) {
    const wantedPageId = FUEL_PAGE_IDS[days];

    for (const row of categorizedProducts.value || []) {
        const list = Array.isArray(row?.products) ? row.products : [];
        if (!list.length) continue;

        const byPage = list.find(
            (p) => p && p.enabled !== false && Number(p.page_id) === Number(wantedPageId)
        );
        if (byPage) {
            return { product: byPage, categoryRow: row };
        }

        if (durationDaysFromProduct(list[0]) === days) {
            const fuel = pickFuelFromList(list);
            if (fuel) return { product: fuel, categoryRow: row };
        }
    }

    const fromFlat = (products.value || []).find(
        (p) => p && p.enabled !== false && Number(p.page_id) === Number(wantedPageId)
    );
    if (fromFlat) {
        return { product: fromFlat, categoryRow: null };
    }

    const fallback = (products.value || []).find((p) => {
        if (!p || p.enabled === false) return false;
        if (!isFuelProduct(p)) return false;
        return durationDaysFromProduct(p) === days;
    }) || null;

    return { product: fallback, categoryRow: null };
}

function durationDaysFromProduct(product) {
    if (!product || typeof product !== 'object') return null;

    if ('duration' in product) {
        const raw = product.duration;
        if (raw === null || raw === undefined || raw === '') return 1;
        const d = Number(raw);
        if (!Number.isFinite(d)) return null;
        if (d === 0 || d === 1) return 1;
        if (d === 9 || d === 10) return 10;
        if (d === 29 || d === 30) return 30;
        if (d === 364 || d === 365 || d === 366) return 365;
    }

    if (typeof product.duration_days === 'number') {
        const d = product.duration_days;
        if (d === 1 || d === 10 || d === 30 || d === 365) return d;
    }

    return null;
}

function isFuelProduct(product) {
    const t = String(product?.translated?.title ?? product?.title ?? product?.name ?? '').toLowerCase();
    if (!t) return true;
    if (t.includes('cng') || t.includes('biomethane') || t.includes('electric')) {
        return false;
    }
    if (/hybrid/.test(t) && /plug|plugin/.test(t)) {
        return false;
    }
    return true;
}

function pickFuelFromList(list) {
    const enabled = (list || []).filter((p) => p && p.enabled !== false);
    if (!enabled.length) return null;
    const petrol = enabled.find((p) => {
        const t = String(p?.translated?.title ?? p?.title ?? '').toLowerCase();
        return (
            t.includes('petrol')
            || t.includes('diesel')
            || t.includes('benzine')
            || t.includes('benzin')
            || t.includes('gasoline')
            || t.includes('essence')
            || t.includes('бензин')
            || t.includes('дизел')
        );
    });
    return petrol || enabled[0];
}

function resolveLabelFromApi(product, categoryRow) {
    const fromCategory = cleanTitle(categoryRow?.translated?.title);
    if (fromCategory) return fromCategory;

    const fromProductCategory = cleanTitle(product?.category?.translated?.title);
    if (fromProductCategory) return fromProductCategory;

    const fromProduct = cleanTitle(product?.translated?.title ?? product?.title);
    if (fromProduct) return fromProduct;

    return '';
}

/** Official, fee, total — mirrors VignettePriceRows.php / productPricing.js */
function getPriceParts(product) {
    if (!product || typeof product !== 'object') {
        return { official: 0, fee: 0, total: 0 };
    }

    const official = getProductBasePrice(product);
    const fee = getProductServiceFee(product);

    return {
        official,
        fee,
        total: getProductTotalWithFees(product),
    };
}

/**
 * Remove "Vignette" prefix
 */
function cleanTitle(title) {
    if (title == null) return '';
    return String(title)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/^Vignette\s*/i, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Price formatter
 */
function formatPrice(amount) {
    const value = Number(amount) || 0;

    return `${currencySymbol.value}${value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`;
}

/**
 * Determine icon by duration in days
 */
function pickIcon(days) {
    if (days === 1) return 'today';
    if (days === 10) return 'calendar_view_week';
    if (days === 30) return 'calendar_month';
    if (days >= 365) return 'event_repeat';
    return 'local_shipping';
}

/**
 * Buy button
 */
async function onBuy(row) {
    if (!row?.id) return;

    store.commit('form/SET_FORM_DATA', {
        product_id: row.id,
        selected_duration: row.days ?? null,
    });

    store.dispatch('form/saveDataToLocalStorage');

    window.location.href = buyUrl.value;
}
/**
 * Load data on mount
 */
function hideStaticTable() {
    document.querySelector('[data-vignette-prices-static="1"]')?.setAttribute('hidden', '');
}

async function refreshCatalogForCurrency() {
    pricesLoading.value = true;
    try {
        await store.dispatch('form/reloadProductsCatalog');
    } catch {
        // keep SSR table if API fails
    } finally {
        pricesLoading.value = false;
    }
}

watch(
    () => String(storeCurrency.value?.code ?? '').trim().toUpperCase(),
    (code, prev) => {
        if (code && prev && code !== prev) {
            refreshCatalogForCurrency();
        }
    }
);

onMounted(async () => {
    await refreshCatalogForCurrency();

    hideStaticTable();
    ready.value = true;
    document
        .querySelector('.cv-vignette-prices-slot vignette-prices')
        ?.classList.add('cv-vignette-prices-ready');
});
</script>
